// src/pages/ProductsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { productsAPI, warehousesAPI, companiesAPI } from '../services/api';
import { Card, Badge, SectionHeader, EmptyState, Button, Modal, Input, Select, Textarea, ConfirmModal } from '../components/ui';
import toast from 'react-hot-toast';
import { Package, Plus, Tag, Download, ChevronDown, Pencil, Trash2, Printer, Search } from 'lucide-react';
import styles from './ProductsPage.module.css';

const TRACKING_TYPES = [
    { value: 'UNIT', label: 'Unit — Each Tag = N Discrete Items' },
    { value: 'PACKAGE', label: 'Package — Tag on a Container (Box, Bundle)' },
    { value: 'WEIGHT', label: 'Weight — Tag on Organic/Bulk Product' },
];
const WEIGHT_UNITS = ['KG', 'LB', 'Quintal', 'OZ', 'G'];
const PACKAGE_TYPES = ['Box', 'Bundle', 'Pallet', 'Case', 'Bag', 'Bale'];
const BRIDGE_URL_KEY = 'gaafrfid_bridge_print_url';
function getBridgeUrl() { return localStorage.getItem(BRIDGE_URL_KEY) || ''; }
function saveBridgeUrl(url) { localStorage.setItem(BRIDGE_URL_KEY, url); }

export default function ProductsPage() {
    const { companyId, isGaafAdmin } = useAuth();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [encodeTarget, setEncodeTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    useEffect(() => {
        if (!isGaafAdmin) return;
        companiesAPI.list().then(res => {
            const list = res.data.companies || [];
            setCompanies(list);
            if (list.length > 0) setSelectedCompanyId(list[0].companyId);
        }).catch(() => { });
    }, [isGaafAdmin]);

    const effectiveCompanyId = isGaafAdmin ? selectedCompanyId : companyId;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await productsAPI.list(effectiveCompanyId || (isGaafAdmin ? undefined : companyId));
            setProducts((res.data.products || []).filter(p => p.status !== 'inactive'));
        } catch (_) { }
        setLoading(false);
    }, [effectiveCompanyId, companyId, isGaafAdmin]);

    useEffect(() => { load(); }, [load]);

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleteLoading(true);
        try {
            await productsAPI.delete(deleteTarget.productId);
            toast.success('Product deactivated');
            setDeleteTarget(null);
            load();
        } catch (err) { toast.error(err?.response?.data?.message || 'Failed to Delete Product'); }
        setDeleteLoading(false);
    };

    const filtered = products.filter(p =>
        !search ||
        p.name?.toLowerCase().includes(search.toLowerCase()) ||
        p.sku?.toLowerCase().includes(search.toLowerCase()) ||
        p.industry?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className={`${styles.page} animate-fadeIn`}>
            <SectionHeader
                title="Products"
                description="Define Products and Configure RFID Tracking"
                actions={
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        {isGaafAdmin && companies.length > 0 && (
                            <select value={selectedCompanyId} onChange={e => setSelectedCompanyId(e.target.value)}
                                style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--text-primary)', padding: '7px 12px', fontFamily: 'var(--font-body)', fontSize: '0.875rem', outline: 'none' }}>
                                {companies.map(c => <option key={c.companyId} value={c.companyId}>{c.name}</option>)}
                            </select>
                        )}
                        <Button variant="primary" icon={<Plus size={15} />} onClick={() => setShowCreate(true)}>Add Product</Button>
                    </div>
                }
            />

            <div style={{ position: 'relative', maxWidth: 360, marginBottom: 16 }}>
                <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', pointerEvents: 'none' }} />
                <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search by Name, SKU, Industry…"
                    style={{ width: '100%', paddingLeft: 34, paddingRight: 12, paddingTop: 8, paddingBottom: 8, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', color: 'var(--text-primary)', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' }}
                />
            </div>

            {loading ? (
                <div className={styles.grid}>{[1, 2, 3, 4].map(i => <SkeletonProduct key={i} />)}</div>
            ) : filtered.length === 0 ? (
                <EmptyState
                    icon={<Package size={40} />}
                    title={search ? 'No Products match your search' : 'No Products yet'}
                    description={search ? 'Try a different Name, SKU, or Industry.' : 'Create your first Product to start Encoding RFID Tags.'}
                    action={!search && <Button variant="primary" icon={<Plus size={15} />} onClick={() => setShowCreate(true)}>Add Product</Button>}
                />
            ) : (
                <div className={styles.grid}>
                    {filtered.map((p) => (
                        <ProductCard key={p.productId} product={p}
                            onEdit={() => setEditTarget(p)} onDelete={() => setDeleteTarget(p)} onEncode={() => setEncodeTarget(p)} />
                    ))}
                </div>
            )}

            <CreateProductModal open={showCreate} companyId={effectiveCompanyId} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
            <CreateProductModal open={!!editTarget} companyId={editTarget?.companyId || effectiveCompanyId} product={editTarget} onClose={() => setEditTarget(null)} onCreated={() => { setEditTarget(null); load(); }} />
            <ConfirmModal open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete} loading={deleteLoading}
                title="Delete Product" message={`Delete "${deleteTarget?.name}"? This will Deactivate the Product. All existing Inventory Data and Tag Reads are preserved.`} confirmLabel="Delete Product" />
            <EncodeModal product={encodeTarget} open={!!encodeTarget} onClose={() => setEncodeTarget(null)} />
        </div>
    );
}

function ProductCard({ product, onEdit, onDelete, onEncode }) {
    const trackingLabels = { UNIT: 'Unit', PACKAGE: 'Package', WEIGHT: 'Weight' };
    const trackingVariants = { UNIT: 'info', PACKAGE: 'neutral', WEIGHT: 'success' };
    return (
        <Card className={styles.productCard} hover>
            <div className={styles.productHeader}>
                <div className={styles.productIcon}><Package size={18} /></div>
                <div className={styles.productMeta}>
                    <h4 className={styles.productName}>{product.name}</h4>
                    <code className={styles.productSku}>{product.sku}</code>
                </div>
                <Badge variant={trackingVariants[product.trackingType]} size="sm">{trackingLabels[product.trackingType] || product.trackingType}</Badge>
            </div>
            <div className={styles.productDetails}>
                {product.trackingType === 'UNIT' && <div className={styles.detail}><span className={styles.detailLabel}>Units per Tag</span><span className={styles.detailValue}>{product.unitsPerTag || 1} {product.displayUnit}</span></div>}
                {product.trackingType === 'PACKAGE' && <div className={styles.detail}><span className={styles.detailLabel}>Package</span><span className={styles.detailValue}>{product.unitsPerPackage} {product.displayUnit} / {product.packageType}</span></div>}
                {product.trackingType === 'WEIGHT' && <div className={styles.detail}><span className={styles.detailLabel}>Weight / Tag</span><span className={styles.detailValue}>{product.weightPerTag} {product.weightUnit} {product.trackMerma ? '(Merma)' : ''}</span></div>}
                {product.lowStockThreshold > 0 && <div className={styles.detail}><span className={styles.detailLabel}>Low Stock Alert</span><span className={styles.detailValue}>≤ {product.lowStockThreshold} {product.displayUnit}</span></div>}
                <div className={styles.detail}><span className={styles.detailLabel}>Last Serial</span><span className={styles.detailValue}>{product.lastSerialNumber || 0}</span></div>
                {product.industry && <div className={styles.detail}><span className={styles.detailLabel}>Industry</span><span className={styles.detailValue}>{product.industry}</span></div>}
            </div>
            <div className={styles.productActions}>
                <Button variant="secondary" size="sm" icon={<Pencil size={13} />} onClick={onEdit}>Edit</Button>
                <Button variant="danger" size="sm" icon={<Trash2 size={13} />} onClick={onDelete}>Delete</Button>
                <Button variant="primary" size="sm" icon={<Tag size={13} />} onClick={onEncode} fullWidth>Encode Tags</Button>
            </div>
        </Card>
    );
}

function CreateProductModal({ open, companyId, product, onClose, onCreated }) {
    const { isGaafAdmin } = useAuth();
    const isEditing = !!product;
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [form, setForm] = useState({ name: '', sku: '', description: '', industry: '', trackingType: 'UNIT', unitsPerTag: 1, displayUnit: 'Units', packageType: 'Box', unitsPerPackage: 25, weightUnit: 'KG', weightPerTag: 1, trackMerma: false, lowStockThreshold: 0, expiryDays: '' });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !isGaafAdmin) return;
        companiesAPI.list().then(res => {
            const list = res.data.companies || [];
            setCompanies(list);
            if (list.length > 0 && !selectedCompanyId) setSelectedCompanyId(list[0].companyId);
        }).catch(() => { });
    }, [open, isGaafAdmin]);

    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    useEffect(() => {
        if (open) {
            if (product) {
                setForm({ name: product.name || '', sku: product.sku || '', description: product.description || '', industry: product.industry || '', trackingType: product.trackingType || 'UNIT', unitsPerTag: product.unitsPerTag || 1, displayUnit: product.displayUnit || 'Units', packageType: product.packageType || 'Box', unitsPerPackage: product.unitsPerPackage || 25, weightUnit: product.weightUnit || 'KG', weightPerTag: product.weightPerTag || 1, trackMerma: product.trackMerma || false, lowStockThreshold: product.lowStockThreshold || 0, expiryDays: product.expiryDays || '' });
            } else {
                setForm({ name: '', sku: '', description: '', industry: '', trackingType: 'UNIT', unitsPerTag: 1, displayUnit: 'Units', packageType: 'Box', unitsPerPackage: 25, weightUnit: 'KG', weightPerTag: 1, trackMerma: false, lowStockThreshold: 0, expiryDays: '' });
            }
            setLoading(false);
        }
    }, [open, product?.productId]);

    const effectiveCompanyId = isGaafAdmin ? selectedCompanyId : companyId;

    const handleSubmit = async () => {
        if (!form.name || !form.sku) return toast.error('Name and SKU are Required');
        if (!effectiveCompanyId) return toast.error('Select a Company first');
        setLoading(true);
        try {
            const payload = { ...form, companyId: effectiveCompanyId, lowStockThreshold: parseInt(form.lowStockThreshold) || 0, expiryDays: form.expiryDays ? parseInt(form.expiryDays) : null, unitsPerTag: parseInt(form.unitsPerTag) || 1, unitsPerPackage: parseInt(form.unitsPerPackage) || 1, weightPerTag: parseFloat(form.weightPerTag) || 1 };
            if (isEditing) { await productsAPI.update(product.productId, payload); toast.success('Product Updated'); }
            else { await productsAPI.create(payload); toast.success('Product Created'); }
            onCreated();
        } catch (err) { toast.error(err?.response?.data?.message || 'Failed to Create Product'); }
        setLoading(false);
    };

    return (
        <Modal open={open} onClose={onClose} title={isEditing ? `Edit — ${product.name}` : "Add Product"} size="lg">
            <div className={styles.formGrid}>
                {isGaafAdmin && (
                    <div className={styles.fullSpan}>
                        <Select label="Company *" value={selectedCompanyId} onChange={e => setSelectedCompanyId(e.target.value)}>
                            <option value="">Select Company…</option>
                            {companies.map(c => <option key={c.companyId} value={c.companyId}>{c.name}</option>)}
                        </Select>
                    </div>
                )}
                <Input label="Product Name *" value={form.name} onChange={e => set('name', e.target.value)} placeholder="GAAF Robusto" />
                <Input label="SKU *" value={form.sku} onChange={e => set('sku', e.target.value)} placeholder="TOB-GAAF-ROB-001" />
                <Input label="Industry" value={form.industry} onChange={e => set('industry', e.target.value)} placeholder="Tobacco" />
                <Textarea label="Description" value={form.description} onChange={e => set('description', e.target.value)} placeholder="Optional Description" style={{ minHeight: 72 }} />
                <div className={styles.fullSpan}>
                    <Select label="Tracking Type *" value={form.trackingType} onChange={e => set('trackingType', e.target.value)}>
                        {TRACKING_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </Select>
                </div>
                {form.trackingType === 'UNIT' && (<><Input label="Units per Tag" type="number" min="1" value={form.unitsPerTag} onChange={e => set('unitsPerTag', e.target.value)} /><Input label="Display Unit" value={form.displayUnit} onChange={e => set('displayUnit', e.target.value)} placeholder="Units, Cigars, Bottles…" /></>)}
                {form.trackingType === 'PACKAGE' && (<><Select label="Package Type" value={form.packageType} onChange={e => set('packageType', e.target.value)}>{PACKAGE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</Select><Input label="Units per Package" type="number" min="1" value={form.unitsPerPackage} onChange={e => set('unitsPerPackage', e.target.value)} /><Input label="Display Unit" value={form.displayUnit} onChange={e => set('displayUnit', e.target.value)} placeholder="Cigars, Units…" /></>)}
                {form.trackingType === 'WEIGHT' && (<><Select label="Weight Unit" value={form.weightUnit} onChange={e => set('weightUnit', e.target.value)}>{WEIGHT_UNITS.map(u => <option key={u} value={u}>{u}</option>)}</Select><Input label="Nominal Weight per Tag" type="number" step="0.001" min="0" value={form.weightPerTag} onChange={e => set('weightPerTag', e.target.value)} /><div className={styles.checkRow}><input type="checkbox" id="merma" checked={form.trackMerma} onChange={e => set('trackMerma', e.target.checked)} /><label htmlFor="merma">Enable Merma (Moisture Loss) Tracking</label></div></>)}
                <Input label="Low Stock Threshold" type="number" min="0" value={form.lowStockThreshold} onChange={e => set('lowStockThreshold', e.target.value)} hint="Email Alert sent when Stock falls below this" />
                <Input label="Expiry Days" type="number" min="0" value={form.expiryDays} onChange={e => set('expiryDays', e.target.value)} hint="Days until expiry (Printed on label)" />
                <div className={`${styles.fullSpan} ${styles.modalActions}`}>
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" loading={loading} onClick={handleSubmit}>{isEditing ? 'Save Changes' : 'Create Product'}</Button>
                </div>
            </div>
        </Modal>
    );
}

function EncodeModal({ product, open, onClose }) {
    const { companyId, isGaafAdmin } = useAuth();
    const [warehouses, setWarehouses] = useState([]);
    const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [batchNumber, setBatchNumber] = useState(`B-${new Date().getFullYear()}-001`);
    const [weight, setWeight] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [bridgeUrl, setBridgeUrlState] = useState(getBridgeUrl);
    const [printing, setPrinting] = useState(false);
    const [showBridgeInput, setShowBridgeInput] = useState(false);

    useEffect(() => {
        if (open && product) {
            setQuantity(1); setBatchNumber(`B-${new Date().getFullYear()}-001`); setWeight(''); setResult(null); setLoading(false); setPrinting(false); setShowBridgeInput(false);
            const cid = product.companyId || companyId;
            warehousesAPI.list(cid).then(res => {
                const list = (res.data.warehouses || []).filter(w => w.status !== 'inactive');
                setWarehouses(list);
                setSelectedWarehouseId(list[0]?.warehouseId || '');
            }).catch(() => { });
        }
    }, [open, product?.productId]);

    if (!product) return null;
    const selectedWarehouse = warehouses.find(w => w.warehouseId === selectedWarehouseId);

    const handleEncode = async () => {
        if (quantity < 1 || quantity > 5000) return toast.error('Quantity must be 1–5000');
        if (!selectedWarehouseId) return toast.error('Select a Warehouse');
        setLoading(true);
        try {
            const res = await productsAPI.encodeTag(product.productId, { quantity: parseInt(quantity), batchNumber, warehouseId: selectedWarehouseId, warehouseName: selectedWarehouse?.name || undefined, weight: weight || undefined });
            setResult(res.data);
            toast.success(`${quantity} tag(s) Encoded Successfully`);
        } catch (err) { toast.error(err?.response?.data?.message || 'Encoding Failed'); }
        setLoading(false);
    };

    const downloadZPL = () => {
        if (!result?.labels) return;
        const content = result.labels.map(l => l.zpl).join('\n');
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `${product.sku}-${batchNumber}-labels.zpl`; a.click();
        URL.revokeObjectURL(url);
    };

    const printLabels = async () => {
        if (!result?.labels) return;
        const url = bridgeUrl.trim();
        if (!url) { setShowBridgeInput(true); return toast.error('Enter the Bridge PC IP Address first'); }
        const zplContent = result.labels.map(l => l.zpl).join('\n');
        setPrinting(true);
        try {
            const printUrl = url.startsWith('http') ? `${url.replace(/\/$/, '')}/print` : `http://${url}:9200/print`;
            const resp = await fetch(printUrl, { method: 'POST', headers: { 'Content-Type': 'application/octet-stream' }, body: zplContent });
            if (resp.ok) { toast.success(`${result.quantity} label(s) sent to printer`); }
            else { throw new Error(`Bridge returned ${resp.status}`); }
        } catch (err) { toast.error(`Print Failed: ${err.message}. Is the bridge running?`); setShowBridgeInput(true); }
        setPrinting(false);
    };

    const handleBridgeUrl = (val) => { setBridgeUrlState(val); saveBridgeUrl(val); };
    const resetAndClose = () => { setResult(null); setQuantity(1); onClose(); };

    return (
        <Modal open={open} onClose={resetAndClose} title={`Encode Tags — ${product.name}`} size="md">
            {!result ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <p style={{ fontSize: '0.875rem', color: 'var(--text-tertiary)' }}>Generates EPC Codes and ZPL Label Data to Send to your SATO CT4-LX Printer.</p>
                    <Input label="Quantity" type="number" min="1" max="5000" value={quantity} onChange={e => setQuantity(e.target.value)} />
                    <Input label="Batch Number" value={batchNumber} onChange={e => setBatchNumber(e.target.value)} />
                    <div>
                        <label style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Warehouse *</label>
                        <select value={selectedWarehouseId} onChange={e => setSelectedWarehouseId(e.target.value)} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--text-primary)', padding: '9px 12px', fontFamily: 'var(--font-body)', fontSize: '0.875rem', width: '100%', outline: 'none' }}>
                            <option value="">Select Warehouse…</option>
                            {warehouses.map(w => <option key={w.warehouseId} value={w.warehouseId}>{w.name}{w.city ? ` — ${w.city}` : ''}</option>)}
                        </select>
                    </div>
                    {product.trackingType === 'WEIGHT' && <Input label="Weight (printed on label)" value={weight} onChange={e => setWeight(e.target.value)} placeholder={`e.g. 1 ${product.weightUnit}`} />}
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                        <Button variant="secondary" onClick={resetAndClose}>Cancel</Button>
                        <Button variant="primary" loading={loading} icon={<Tag size={14} />} onClick={handleEncode}>Generate {quantity} Label{quantity > 1 ? 's' : ''}</Button>
                    </div>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div className={styles.encodeResult}>
                        <div className={styles.encodeResultStat}><span className={styles.encodeNum}>{result.quantity}</span><span className={styles.encodeLabel}>Labels Encoded</span></div>
                        <div className={styles.encodeResultStat}><span className={styles.encodeNum} style={{ fontSize: '0.85rem' }}>{result.firstEpc?.slice(-8)}</span><span className={styles.encodeLabel}>First EPC (Last 8)</span></div>
                        <div className={styles.encodeResultStat}><span className={styles.encodeNum} style={{ fontSize: '0.85rem' }}>{result.batch}</span><span className={styles.encodeLabel}>Batch</span></div>
                    </div>
                    {showBridgeInput && (
                        <div className={styles.bridgeRow}>
                            <Input label="Bridge PC IP Address" value={bridgeUrl} onChange={e => handleBridgeUrl(e.target.value)} placeholder="192.168.0.105" hint="IP of the PC running bridge.py — port 9200 must be reachable from this browser" />
                            <Button variant="secondary" size="sm" onClick={() => setShowBridgeInput(false)} style={{ marginTop: 20 }}>OK</Button>
                        </div>
                    )}
                    <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                        {bridgeUrl ? <>Print directly to the SATO printer via the bridge, or download the ZPL file.</> : <>Download the ZPL file and send it to your SATO CT4-LX printer, or configure the bridge IP to print directly.</>}
                    </p>
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <Button variant="secondary" onClick={resetAndClose}>Done</Button>
                        {!showBridgeInput && !bridgeUrl && <Button variant="ghost" size="sm" onClick={() => setShowBridgeInput(true)}>Configure Printer</Button>}
                        <Button variant="secondary" icon={<Download size={14} />} onClick={downloadZPL}>Download ZPL</Button>
                        <Button variant="primary" icon={<Printer size={14} />} loading={printing} onClick={printLabels}>Print {result.quantity} Label{result.quantity > 1 ? 's' : ''}</Button>
                    </div>
                </div>
            )}
        </Modal>
    );
}

function SkeletonProduct() {
    return (
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: 20 }}>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                <div className="skeleton" style={{ width: 36, height: 36, borderRadius: 8 }} />
                <div style={{ flex: 1 }}><div className="skeleton" style={{ height: 16, width: '60%', marginBottom: 6 }} /><div className="skeleton" style={{ height: 12, width: '40%' }} /></div>
            </div>
            <div className="skeleton" style={{ height: 12, width: '80%', marginBottom: 8 }} />
            <div className="skeleton" style={{ height: 12, width: '60%', marginBottom: 16 }} />
            <div className="skeleton" style={{ height: 34, borderRadius: 8 }} />
        </div>
    );
}
